import { SetStateAction, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { uniqBy } from 'lodash';

import {
  FileTreeItemType,
  Ryecon,
  TreeNodeValues,
  useOpenDirectories,
} from '@dbt-labs/sourdough';

import { DataPlatform, WarehouseEntityType } from '@/__generated__/graphql';
import { useLinkToExplorer } from '@/dbt-explorer';
import { useFavoriteAssets } from '@/hooks/admin/favoriteAssets';
import { useLazyAvailablePlatforms } from '@/hooks/discovery/catalog/availablePlatforms';
import {
  useLazyWarehouseEntities,
  useLazyWarehouseEntity,
  WarehouseEntitiesParams,
} from '@/hooks/discovery/catalog/entities';
import { useValidatedParams } from '@/hooks/params';
import { useDbtCloudAccount } from '../DbtCloudProvider';
import { AssetDataType, TreeData } from './types';
import {
  generateFileWithLogo,
  generateFolder,
  generateFolderWithIcon,
  isWarehouseAssetId,
  mergeTreeItems,
  parseAssetId,
  scrollToPath,
  useMenuActions,
  WarehouseAssetTypeIconMapping,
  WarehouseIconMapping,
} from './utils';

const TableViewAssetMapping = {
  tables: WarehouseEntityType.Table,
  views: WarehouseEntityType.View,
};

const getIconOverride = (
  entityType: string,
  name: string,
): { ryecon: Ryecon; label: string } | undefined => {
  const icon =
    WarehouseAssetTypeIconMapping[
      entityType as keyof typeof WarehouseAssetTypeIconMapping
    ];
  if (icon) {
    return {
      ryecon: icon,
      label: name,
    };
  }
  return undefined;
};

const MAX_PAGE_SIZE = 10;

export const useWarehouseTree = () => {
  const { accountId, hasAccountFeature } = useDbtCloudAccount();
  const shouldFetchWarehouseData =
    hasAccountFeature('catalogIngestion') && hasAccountFeature('explorerAccountUI');
  const [after, setAfter] = useState<Record<string, string>>({});
  const [queryParams] = useSearchParams();
  const { assetId } = useValidatedParams();

  const tab = queryParams.get('tab');
  const [selectedFolder, setSelectedFolder] = useState<string | undefined>(undefined);

  const [, getEntity] = useLazyWarehouseEntity();

  const navigate = useNavigate();
  const catalogAssetLink = useLinkToExplorer('catalogAsset');

  const [selectedFile, setSelectedFile] = useState<string>();
  const [fileTree, setFileTree] = useState<FileTreeItemType[]>([]);

  const { openDirectories, setOpenDirectories } = useOpenDirectories({
    items: fileTree,
    activeFilePath: selectedFile,
    rootNodeName: 'root',
  });

  const [, getAvailablePlatforms] = useLazyAvailablePlatforms();

  const [catalogEntities, getEntities] = useLazyWarehouseEntities();
  const [allEntities, setAllEntities] = useState<AssetDataType[]>([]);
  const [initialized, setInitialized] = useState(false);
  const { getMenuActions } = useMenuActions();
  const { data: favoriteAssets } = useFavoriteAssets(accountId ?? 0);

  const initializeTree = useCallback(async () => {
    const { data: platforms } = await getAvailablePlatforms({
      accountId: accountId ?? 0,
    });

    const entitiesPromises = platforms?.map((platform) => {
      return getEntities({
        first: MAX_PAGE_SIZE,
        accountId: accountId ?? 0,
        filter: {
          parent_urn: null,
          type: WarehouseEntityType.Connection,
          urn: null,
          platform,
        },
      });
    });
    const results = await Promise.all(entitiesPromises || []);
    if (results.length > 0) {
      const warehouseNonEmptyPlatforms = new Set(
        results
          .filter((res) => res.data.length > 0)
          .map((res) => res.data[0].platform)
          .filter((platform) => !!platform),
      ) as Set<DataPlatform>;
      const warehouseNodes = Array.from(warehouseNonEmptyPlatforms).map((platform) => {
        return generateFolderWithIcon(platform, {
          iconOverride: {
            ryecon: WarehouseIconMapping[platform],
            label: platform,
          },
          name: platform,
          data: {
            pagination: {
              page: 0,
              hasMore: false,
              isLoading: false,
              total: 0,
            },
          },
        });
      });
      setFileTree((prev) => mergeTreeItems(prev, warehouseNodes));
    }
    setInitialized(true);
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!shouldFetchWarehouseData) {
      return;
    }
    initializeTree();
    // we only want to check at first load if there are any entities
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSort = useCallback(
    (a: string, b: string) => {
      // we remove the first character since it's added by the file tree itself
      const parsedAIndex = a
        .split('/')
        .filter((id) => !!id)
        .pop()
        ?.substring(1);
      const parsedBIndex = b
        .split('/')
        .filter((id) => !!id)
        .pop()
        ?.substring(1);
      // if the index is the warehouse type, we keep the order
      if (!isWarehouseAssetId(parsedAIndex) || !isWarehouseAssetId(parsedBIndex)) {
        return 0;
      }
      const aIndex = allEntities.findIndex((entity) => entity.urn === parsedAIndex);
      const bIndex = allEntities.findIndex((entity) => entity.urn === parsedBIndex);
      // if not found, we want to sort them to the end
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    },
    [allEntities],
  );

  const setTreeStateForPath = useCallback(
    (path: string, props: Partial<TreeNodeValues['pagination']>) => {
      setFileTree((prev) => {
        const newTree = [...prev];
        const updatedNode = newTree.find((node) => node.id === path);
        if (!updatedNode) return prev;
        updatedNode.data.pagination = {
          hasMore: props?.hasMore ?? updatedNode.data.pagination?.hasMore ?? false,
          page: props?.page ?? updatedNode.data.pagination?.page ?? 0,
          total: props?.total ?? updatedNode.data.pagination?.total ?? 0,
          isLoading:
            props?.isLoading ?? updatedNode.data.pagination?.isLoading ?? false,
        };
        return newTree;
      });
    },
    [],
  );

  const navigateToCatalogAsset = useCallback(
    (folder: string | undefined) => {
      if (!folder) return;
      const path = folder.split('/').pop();
      // if it's a table or view we use the parent folder (schema) and add the tab to the url
      if (path === 'tables' || path === 'views') {
        const parentFolder = folder.split('/').slice(0, -1).pop();
        const pathWithSearchParams = `${parentFolder}?tab=${path}`;
        navigate(
          catalogAssetLink({
            accountId: accountId ?? 0,
            assetId: pathWithSearchParams ?? '',
          }),
        );
        // Remove this after we adjust the BE to return the warehouse info
      } else {
        navigate(
          catalogAssetLink({
            accountId: accountId ?? 0,
            assetId: path ?? '',
          }),
        );
      }
    },
    [accountId, navigate, catalogAssetLink],
  );

  const getTooltipText = useCallback(
    (entityName: string, path: string) => {
      const parts = path.split('/');
      const resolvedParts = parts.map((part) => {
        const entityInTree = fileTree.find((node) => {
          const id = node.id.split('/').pop();
          return id === part;
        });
        if (entityInTree) {
          return entityInTree.data.name;
        }
        return part;
      });
      resolvedParts.push(entityName);
      return resolvedParts.join('/');
    },
    [fileTree],
  );

  // update the tree with the favorite actions
  useEffect(() => {
    if (initialized) {
      setFileTree((prev) => {
        const newTree = prev.map((node) => {
          const assetId = node.id.split('/').pop();
          const isTableOrViewFolder = ['tables', 'views'].includes(assetId ?? '');

          if (!assetId || isTableOrViewFolder || node.parent === 'root') return node;
          const platform = node.id.split('/')[0];
          return {
            ...node,
            data: {
              ...node.data,
              actions: getMenuActions({
                type: 'asset',
                fileTreeId: node.id,
                assetId,
                name: node.data.rawData?.name ?? '',
                assetType: node.data.rawData?.assetType ?? '',
                platform,
              }),
            },
          };
        });
        return newTree;
      });
    }
    // we only need to check for the favorite projects and the active state
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoriteAssets, initialized]);

  const generateFileTreeItem = useCallback(
    (entity: AssetDataType, path: string) => {
      const name = entity.name || '';
      const urn = entity.urn;
      const relativePath = `${path}/${urn}`;
      const platform = path.split('/')[0];
      const actions = getMenuActions?.({
        name,
        assetType: entity.type,
        assetId: entity.urn,
        type: 'asset',
        fileTreeId: relativePath,
        platform,
      });
      const iconOverride = getIconOverride(entity.type, name);

      if (['Connection', 'Database', 'Schema'].includes(entity.type)) {
        return generateFolderWithIcon(relativePath, {
          iconOverride,
          name,
          data: {
            tooltipText: getTooltipText(name, path),
            actions,
          },
        });
      } else if (entity.type === 'Table' || entity.type === 'View') {
        return generateFileWithLogo(relativePath, {
          iconOverride,
          name,
          data: {
            tooltipText: getTooltipText(name, path),
            actions,
            rawData: {
              assetType: entity.type,
              name,
            },
          },
        });
      }

      return generateFolder(relativePath, {
        name: entity.name ?? '',
        data: {
          tooltipText: getTooltipText(name, path),
        },
      });
    },
    [getMenuActions, getTooltipText],
  );

  const updateFileTree = useCallback(
    (currentData: Omit<typeof catalogEntities, 'fetchMore'>, path: string) => {
      if (!currentData || !currentData.data) return;

      const items: FileTreeItemType[] = currentData.data.map((entity) =>
        generateFileTreeItem(entity as AssetDataType, path),
      );

      if (currentData.pageInfo?.hasNextPage) {
        setAfter((prev) => ({
          ...prev,
          [path]: currentData.pageInfo?.endCursor || '',
        }));
      }

      setFileTree((prev) => {
        const newItems = mergeTreeItems(prev, items);
        const updatedNode = newItems.find((node) => node.id === path);
        if (!updatedNode) return prev;
        updatedNode.data.pagination = {
          page: 0,
          total: currentData.pageInfo?.totalCount ?? 0,
          hasMore: currentData.pageInfo?.hasNextPage ?? false,
          isLoading: false,
        };
        return newItems;
      });
    },
    [generateFileTreeItem],
  );

  const generateChildrenForPath = useCallback(
    async (path: string) => {
      setTreeStateForPath(path, { isLoading: true, hasMore: false });
      let payload: WarehouseEntitiesParams | null = null;
      const platform = path.split('/')[0] as DataPlatform;
      if (path === platform) {
        payload = {
          first: MAX_PAGE_SIZE,
          accountId: accountId ?? 0,
          filter: {
            parent_urn: null,
            type: WarehouseEntityType.Connection,
            // TODO: we will eventually need to add a URN filter for different warehouse types
            // or have the connection return a type that we then filter on
            urn: null,
            platform,
          },
          after: after[path] || '',
        };
      } else if (path.startsWith(`${platform}/`)) {
        const parts = path.split('/');
        // warehouseType/Warehouse
        if (parts.length === 2) {
          const databaseUrn = parts[1];
          payload = {
            first: MAX_PAGE_SIZE,
            accountId: accountId ?? 0,
            filter: {
              parent_urn: `${databaseUrn}`,
              type: WarehouseEntityType.Database,
              urn: '',
              platform,
            },
            after: after[path] || '',
          };
          // warehouseType/Warehouse/Database
        } else if (parts.length === 3) {
          const schemaUrn = parts[2];
          payload = {
            first: MAX_PAGE_SIZE,
            accountId: accountId ?? 0,
            filter: {
              parent_urn: `${schemaUrn}`,
              type: WarehouseEntityType.Schema,
              urn: '',
              platform,
            },
            after: after[path] || '',
          };
          // warehouseType/Warehouse/Database/Schema
        } else if (parts.length === 4) {
          // add the tables and views folders to the file tree
          setFileTree((prev) => {
            const newTree = [...prev];
            const updatedNode = newTree.find((node) => node.id === path);
            if (!updatedNode) return prev;
            updatedNode.data.pagination = {
              hasMore: false,
              page: 0,
              total: 0,
              isLoading: false,
            };
            return mergeTreeItems([
              ...newTree,
              generateFolderWithIcon(`${path}/tables`, {
                name: 'Tables',
                data: {
                  tooltipText: getTooltipText('Tables', path),
                },
              }),
              generateFolderWithIcon(`${path}/views`, {
                name: 'Views',
                data: {
                  tooltipText: getTooltipText('Views', path),
                },
              }),
            ]);
          });
          // warehouseType/Warehouse/Database/Schema/<Table | View>
        } else if (parts.length === 5) {
          const typeUrn = parts[4];
          const schemaUrn = parts[3];
          payload = {
            first: MAX_PAGE_SIZE,
            accountId: accountId ?? 0,
            filter: {
              parent_urn: `${schemaUrn}`,
              type: TableViewAssetMapping[
                typeUrn as keyof typeof TableViewAssetMapping
              ],
              platform: platform as DataPlatform,
              urn: '',
            },
            after: after[path] || '',
          };
        }
      }
      if (payload) {
        const res = await getEntities(payload);
        setAllEntities((prev) =>
          uniqBy([...prev, ...(res.data as AssetDataType[])], 'urn'),
        );
        updateFileTree(res, path);
      }
    },
    [
      setTreeStateForPath,
      accountId,
      after,
      getTooltipText,
      getEntities,
      updateFileTree,
    ],
  );

  const openDirectoryIfNotOpen = useCallback(
    (path: string) => {
      setOpenDirectories((prev) => {
        if (prev?.some((dir) => dir === path)) {
          return prev;
        }
        return [...(prev ?? []), path];
      });
    },
    [setOpenDirectories],
  );

  /**
   * If asset is not present in the file tree, we need to get the asset and update the state
   * If asset is present in the file tree, we just update the state (set selected folder/file, adjust open directories)
   */
  const getAssetAndUpdateState = useCallback(
    async (path: string, selectedAsset: FileTreeItemType | undefined) => {
      let asset = selectedAsset;
      if (!selectedAsset) {
        const currentSelectedAssetId = path.split('/').pop();
        const parentPath = path.split('/').slice(0, -1).join('/');
        if (!currentSelectedAssetId) return;
        // set loading state for the parent path
        setTreeStateForPath(parentPath, { isLoading: true });
        // this means that we are in the tables/views folder and we need to open it
        if (parentPath.split('/').length === 5) {
          openDirectoryIfNotOpen(parentPath);
          // if the parent path has less than the max page size, we need to generate the children
          if (
            fileTree.filter((node) => node.parent === parentPath).length < MAX_PAGE_SIZE
          ) {
            await generateChildrenForPath(parentPath);
          }
        }
        const entity = await getEntity({
          accountId: accountId ?? 0,
          filter: {
            parent_urn: '',
            type: null,
            urn: currentSelectedAssetId,
            platform: null,
          },
        });

        if (!entity.data) return;

        const fileTreeItem = generateFileTreeItem(
          entity.data as AssetDataType,
          parentPath,
        );

        asset = fileTreeItem;

        // set the file tree and remove loading state
        setFileTree((prev) => {
          const newItems = mergeTreeItems(prev, [fileTreeItem]);
          const updatedNode = newItems.find((node) => node.id === parentPath);
          if (!updatedNode) return newItems;
          updatedNode.data.pagination = {
            page: updatedNode.data.pagination?.page ?? 0,
            total: updatedNode.data.pagination?.total ?? 0,
            hasMore: updatedNode.data.pagination?.hasMore ?? false,
            isLoading: false,
          };
          return newItems;
        });
      }

      if (asset?.data?.pathType === 'directory') {
        // if there is no asset at first, we also load it's first page of children
        const shouldLoadChildren = !fileTree.some((node) => node.parent === path);
        if (shouldLoadChildren) {
          generateChildrenForPath(path);
        }
        setSelectedFolder(path);
        openDirectoryIfNotOpen(path);
        scrollToPath(path);
      } else {
        setSelectedFile(path);
        scrollToPath(path);
      }
    },
    [
      accountId,
      fileTree,
      generateChildrenForPath,
      generateFileTreeItem,
      getEntity,
      openDirectoryIfNotOpen,
      setTreeStateForPath,
    ],
  );

  // This hook is used to update the selected folder when the tab changes from outside the tree
  useEffect(() => {
    if (!tab || !initialized) return;
    const pathParts = selectedFolder?.split('/');
    if (!pathParts) return;
    const updatedPathParts =
      pathParts.length === 5
        ? // index 4 is where we have the folder path tables/views
          [...pathParts.slice(0, 4), tab]
        : // if we are at the schema, we just append the tabType to the path
          [...pathParts, tab];
    const newSelectedFolder = updatedPathParts.join('/');

    const selectedAsset = fileTree.find((node) => node.id === newSelectedFolder);
    const isLoaded = fileTree.some((node) => node.parent === newSelectedFolder);
    if (!isLoaded) {
      getAssetAndUpdateState(newSelectedFolder, selectedAsset);
    } else {
      setSelectedFolder(newSelectedFolder);
      openDirectoryIfNotOpen(newSelectedFolder);
      scrollToPath(newSelectedFolder);
    }
    // We only want to run this when the tab changes
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, initialized]);

  const resolveTreeFromAssetId = useCallback(
    async (assetId: string) => {
      const entity = !isWarehouseAssetId(assetId)
        ? await getEntity({
            accountId: accountId ?? 0,
            filter: {
              urn: assetId,
              type: null,
              parent_urn: null,
              platform: null,
            },
          })
        : {
            data: null,
          };

      const platform = entity.data?.platform ?? assetId;
      const paths: string[] = [platform];
      // we generate all the paths from the catalog path
      if (entity.data?.catalogPath?.length) {
        // skip the last one since it is the current entity
        const catalogPaths = entity.data.catalogPath.slice(0, -1).map((_, index) => {
          const pathSegments =
            entity.data?.catalogPath?.slice(0, index + 1).map((item) => item.urn) ?? [];
          return [platform, ...pathSegments].join('/');
        });
        paths.push(...catalogPaths);
      }
      // if the assetId is not a warehouse asset, we need to add the current entity to the paths
      if (!isWarehouseAssetId(assetId)) {
        const lastPath = paths[paths.length - 1];
        const entityType = entity.data?.type?.toLowerCase();
        const finalPath =
          entityType === 'table' || entityType === 'view'
            ? `${lastPath}/${entityType}s/${entity.data?.urn}`
            : `${lastPath}/${entity.data?.urn}`;
        paths.push(finalPath);
        // if the entity is a schema and there is a tab, we need to add the tab to the path
        if (entityType === 'schema' && !!tab) {
          const folderPath = `${lastPath}/${entity.data?.urn}/${tab}`;
          paths.push(folderPath);
        }
      }

      for (const path of paths) {
        // if the path is the warehouse or tables/views, we need to set the loading state and open the directory
        if (
          isWarehouseAssetId(path) ||
          ['tables', 'views'].includes(path.split('/').pop() ?? '')
        ) {
          openDirectoryIfNotOpen(path);
          setSelectedFolder(path);
          scrollToPath(path);
          await generateChildrenForPath(path);
          continue;
        }
        // we fetch the asset if it is not in the file tree or select it from the tree
        const asset = fileTree.find((node) => node.id === path);
        await getAssetAndUpdateState(path, asset);
      }

      // The tree might still be updating, so we do a small delay to ensure the tree is updated
      setTimeout(() => {
        scrollToPath(paths[paths.length - 1], {
          parentSelector: '#side-navigation-container',
          useAbsolutePosition: true,
        });
      }, 300);
    },
    [
      accountId,
      fileTree,
      generateChildrenForPath,
      getAssetAndUpdateState,
      getEntity,
      openDirectoryIfNotOpen,
      tab,
    ],
  );

  useEffect(() => {
    if (!initialized || !assetId) return;
    // get the id from the folder/file structure a/b/urn_id
    const currentSelectedAssetIdFromFolder = selectedFolder?.split('/').pop();
    const currentSelectedAssetIdFromFile = selectedFile?.split('/').pop();
    // if we don't have any selected asset, it means that we are loading an URL with an assetId
    if (
      !currentSelectedAssetIdFromFile &&
      !currentSelectedAssetIdFromFolder &&
      assetId
    ) {
      resolveTreeFromAssetId(assetId);
      return;
    }
    // if the assetId is the same as the current selected asset, we don't need to update the state because we are navigating from the tree
    if (
      (currentSelectedAssetIdFromFolder &&
        currentSelectedAssetIdFromFolder === assetId) ||
      (currentSelectedAssetIdFromFile && currentSelectedAssetIdFromFile === assetId)
    )
      return;
    // get the selected asset from the file tree, it might not exist
    const selectedAsset = fileTree.find((node) => {
      const id = node.id.split('/').pop();
      // there might be cases where we are trying to find the warehouse, so we need to check the id case insensitive
      return id?.toLowerCase() === assetId.toLowerCase();
    });

    // if the selected asset is a directory and there is a tab, we don't want to update the state,
    // because we are navigating from the tree
    if (selectedAsset?.data?.pathType === 'directory' && !!tab) {
      return;
    }

    if (selectedAsset) {
      getAssetAndUpdateState(selectedAsset.id, selectedAsset);
      return;
    }

    let path = selectedFolder;
    // if selected folder path parts length is 4 it means that we are selecting a file from the tree
    // so we must append the tab to the path
    if (selectedFolder?.split('/').length === 4) {
      const tabType = tab === null ? 'tables' : tab;
      path = path?.concat(`/${tabType}`);
    }
    path = path?.concat(`/${assetId}`);

    if (!path) return;
    getAssetAndUpdateState(path, selectedAsset);

    // we only want to run this when the assetId changes
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, initialized]);

  const handleSetSelectedFile = useCallback(
    (value: SetStateAction<string | undefined>) => {
      const newValue = typeof value === 'function' ? value(selectedFile) : value;
      setSelectedFile(newValue);
      const assetId = parseAssetId(newValue);
      if (!assetId) return;
      navigate(
        catalogAssetLink({
          accountId: accountId ?? 0,
          assetId: assetId,
        }),
      );
    },
    [navigate, catalogAssetLink, accountId, selectedFile],
  );

  const handleSetSelectedFolder = useCallback(
    (
      value: SetStateAction<string | undefined>,
      _event?: React.MouseEvent,
      isCollapseClick = false,
    ) => {
      const newValue = typeof value === 'function' ? value(selectedFolder) : value;
      if (!isCollapseClick) {
        setSelectedFolder(newValue);
        navigateToCatalogAsset(newValue);
      }
    },
    [navigateToCatalogAsset, selectedFolder],
  );

  const accountNavProps: TreeData = {
    initialized,
    selectedFile,
    setSelectedFile: handleSetSelectedFile,
    selectedFolder,
    setSelectedFolder: handleSetSelectedFolder,
    fileTree,
    isLoading: catalogEntities.fetching,
    generateChildrenForPath,
    openDirectories,
    setOpenDirectories,
    onSort,
  };

  return accountNavProps;
};
